// The monorail's lift, heard (Sam, 3 Nov, of the lift: "get some noises on
// the lift").
//
// Generated like the piano, no samples, through the one audio context:
//
//   the motor   while the car rides: a low drone (two detuned saws through
//               a lowpass) and the cables' whir (band-passed noise), both
//               rising with the car's speed and falling as it slows
//   the doors   a slide of filtered noise as they part or close, and a
//               soft knock as they meet
//   the chime   two bell notes as the car comes to a landing
//   the button  a short beep under the visitor's finger, not placed
//
// Everything but the button comes from the car, wherever it is, and is
// mixed by the listener's distance to it the way the piano is: the gain
// falls with the square of the distance and a lowpass closes in, so inside
// the car it is a lift and from the square it is nothing at all. The caller
// (MonorailModels in Monorail.jsx) gives each lift's state every frame;
// this module never touches the scene. Sounds are made only after the
// first click has let the page play audio.

import { getCtx, audioReady } from './context'
import { LIFT } from '../world/monorail'

const NEAR = 3
const FAR = 45
const PEAK_SPEED = 1.5 * 15 / (LIFT.rideMs / 1000)    // the ride's fastest, near enough, in m/s

let noise = null
function noiseBuffer(c) {
  if (noise) return noise
  noise = c.createBuffer(1, c.sampleRate, c.sampleRate)
  const d = noise.getChannelData(0)
  for (let i = 0; i < d.length; i++) d[i] = Math.random() * 2 - 1
  return noise
}

// each lift's place in the mix: a gain and a lowpass the distance sets
const lifts = new Map()
function space(c, i) {
  let s = lifts.get(i)
  if (s) return s
  const gain = c.createGain()
  gain.gain.value = 0
  const lp = c.createBiquadFilter()
  lp.type = 'lowpass'
  lp.frequency.value = 800
  gain.connect(lp).connect(c.destination)
  s = { gain, lp, prev: null, lastY: null, motor: null }
  lifts.set(i, s)
  return s
}

function motorOn(c, s) {
  if (s.motor) return
  const out = c.createGain()
  out.gain.value = 0
  const lp = c.createBiquadFilter()
  lp.type = 'lowpass'
  lp.frequency.value = 520
  const a = c.createOscillator(), b = c.createOscillator()
  a.type = 'sawtooth'; b.type = 'sawtooth'
  a.frequency.value = 42; b.frequency.value = 42 * 2.013
  const ga = c.createGain(), gb = c.createGain()
  ga.gain.value = 0.55; gb.gain.value = 0.25
  const whir = c.createBufferSource()
  whir.buffer = noiseBuffer(c)
  whir.loop = true
  const bp = c.createBiquadFilter()
  bp.type = 'bandpass'; bp.frequency.value = 950; bp.Q.value = 0.9
  const gw = c.createGain()
  gw.gain.value = 0.18
  a.connect(ga).connect(lp)
  b.connect(gb).connect(lp)
  lp.connect(out)
  whir.connect(bp).connect(gw).connect(out)
  out.connect(s.gain)
  const t = c.currentTime
  a.start(t); b.start(t); whir.start(t)
  s.motor = { out, a, b, gw }
}

function motorOff(c, s) {
  const m = s.motor
  if (!m) return
  s.motor = null
  const t = c.currentTime
  m.out.gain.cancelScheduledValues(t)
  m.out.gain.setTargetAtTime(0, t, 0.25)
  for (const o of [m.a, m.b]) o.stop(t + 1.5)
  setTimeout(() => { try { m.out.disconnect() } catch { /* gone */ } }, 1800)
}

function slide(c, s, opening) {
  const t = c.currentTime, dur = LIFT.doorMs / 1000
  const src = c.createBufferSource()
  src.buffer = noiseBuffer(c)
  src.loop = true
  const bp = c.createBiquadFilter()
  bp.type = 'bandpass'; bp.Q.value = 1.4
  bp.frequency.setValueAtTime(opening ? 650 : 1300, t)
  bp.frequency.linearRampToValueAtTime(opening ? 1300 : 650, t + dur)
  const g = c.createGain()
  g.gain.setValueAtTime(0, t)
  g.gain.linearRampToValueAtTime(0.16, t + 0.12)
  g.gain.setValueAtTime(0.16, t + dur - 0.2)
  g.gain.linearRampToValueAtTime(0, t + dur)
  src.connect(bp).connect(g).connect(s.gain)
  src.start(t)
  src.stop(t + dur + 0.05)
}

function knock(c, s) {
  const t = c.currentTime
  const o = c.createOscillator()
  o.type = 'sine'
  o.frequency.setValueAtTime(95, t)
  o.frequency.exponentialRampToValueAtTime(48, t + 0.14)
  const g = c.createGain()
  g.gain.setValueAtTime(0.3, t)
  g.gain.exponentialRampToValueAtTime(0.0001, t + 0.2)
  o.connect(g).connect(s.gain)
  o.start(t); o.stop(t + 0.25)
}

// a bell: the note and its inharmonic partial, struck and left to ring
function bell(c, dest, freq, when, gain) {
  for (const [ratio, part] of [[1, 1], [2.756, 0.28], [5.404, 0.08]]) {
    const o = c.createOscillator()
    o.type = 'sine'
    o.frequency.value = freq * ratio
    const g = c.createGain()
    g.gain.setValueAtTime(0, when)
    g.gain.linearRampToValueAtTime(gain * part, when + 0.006)
    g.gain.exponentialRampToValueAtTime(0.0001, when + 1.6 / ratio)
    o.connect(g).connect(dest)
    o.start(when); o.stop(when + 1.7)
  }
}

function chime(c, s) {
  const t = c.currentTime
  bell(c, s.gain, 1318.5, t, 0.2)
  bell(c, s.gain, 1046.5, t + 0.32, 0.2)
}

/**
 * Every frame, for each lift: where it is now (liftNow: phase, y), where
 * its car stands (x, z) and where the listener is. Works out what changed
 * since the last frame and sounds it.
 */
export function hearLift(i, now, car, listener, dt) {
  if (!audioReady()) return
  const c = getCtx()
  const s = space(c, i)
  // the mix: from the listener to the middle of the car
  const d = Math.hypot(listener.x - car.x, listener.y - (now.y + 1.2), listener.z - car.z)
  const f = Math.max(0, Math.min(1, (d - NEAR) / (FAR - NEAR)))
  s.gain.gain.setTargetAtTime((1 - f) * (1 - f), c.currentTime, 0.1)
  s.lp.frequency.setTargetAtTime(500 + (1 - f) * 7500, c.currentTime, 0.15)
  // the motor follows the car's speed
  const speed = s.lastY == null || !dt ? 0 : Math.abs(now.y - s.lastY) / dt
  s.lastY = now.y
  const prev = s.prev
  s.prev = now.phase
  if (prev == null) return   // the first frame: what is going on is already going on
  if (now.phase === 'moving' && prev !== 'moving') motorOn(c, s)
  if (s.motor) {
    const k = Math.min(1, speed / PEAK_SPEED)
    s.motor.out.gain.setTargetAtTime(0.05 + 0.2 * k, c.currentTime, 0.12)
    s.motor.a.frequency.setTargetAtTime(38 + 12 * k, c.currentTime, 0.2)
    s.motor.b.frequency.setTargetAtTime((38 + 12 * k) * 2.013, c.currentTime, 0.2)
    s.motor.gw.gain.setTargetAtTime(0.06 + 0.22 * k, c.currentTime, 0.12)
  }
  if (prev === 'moving' && now.phase !== 'moving') { motorOff(c, s); chime(c, s) }
  if (now.phase === 'opening' && prev !== 'opening') slide(c, s, true)
  if (now.phase === 'closing' && prev !== 'closing') slide(c, s, false)
  if (prev === 'closing' && now.phase === 'shut') knock(c, s)
}

/** The button under a visitor's finger: a short beep, right where they are. */
export function liftBeep() {
  if (!audioReady()) return
  const c = getCtx()
  const t = c.currentTime
  const o = c.createOscillator()
  o.type = 'square'
  o.frequency.value = 1046.5
  const lp = c.createBiquadFilter()
  lp.type = 'lowpass'; lp.frequency.value = 2800
  const g = c.createGain()
  g.gain.setValueAtTime(0, t)
  g.gain.linearRampToValueAtTime(0.06, t + 0.005)
  g.gain.setValueAtTime(0.06, t + 0.07)
  g.gain.linearRampToValueAtTime(0, t + 0.09)
  o.connect(lp).connect(g).connect(c.destination)
  o.start(t); o.stop(t + 0.1)
}
